
import React, { useState, useEffect } from 'react';
import { Book, Loader2, AlertCircle, X, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

interface CommentaryViewerProps {
  passage: string;
  onClose: () => void;
}

interface CommentarySource {
  id: string;
  name: string;
}

const availableSources = [
  { id: 'smith_chuck', name: "Chuck Smith" },
  { id: 'guzik_david', name: "David Guzik" },
  { id: 'mhc', name: "Matthew Henry" },
  { id: 'jfb', name: "Jamieson-Fausset-Brown" }
];

export function CommentaryViewer({ passage, onClose }: CommentaryViewerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentary, setCommentary] = useState<string | null>(null);
  const [commentator, setCommentator] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<string>('mhc');
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);

  useEffect(() => {
    if (passage && selectedSource) {
      fetchCommentary(passage, selectedSource);
    }
  }, [passage, selectedSource]);

  const fetchCommentary = async (passage: string, source: string) => {
    if (!passage) {
      setError('No passage selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setCommentary(null);
      setCommentator(null);

      // Parse passage (e.g., "Genesis 1:1")
      const [book, chapter, verse] = passage.split(/[\s:]+/);
      if (!book || !chapter) {
        throw new Error('Invalid passage format');
      }

      const formattedBook = book.toLowerCase();
      
      // Format the URL based on the source
      let blbUrl = '';
      if (source === 'guzik_david') {
        blbUrl = `https://www.blueletterbible.org/comm/${source}/study-guide/${formattedBook}/${formattedBook}-${chapter}.cfm`;
      } else {
        blbUrl = `https://www.blueletterbible.org/comm/${source}/${formattedBook}/${chapter}.html`;
      }

      console.log('Attempting to fetch commentary from:', blbUrl);
      const proxyUrl = `/api/proxy-commentary?url=${encodeURIComponent(blbUrl)}`;
      console.log('Proxy URL:', proxyUrl);

      const response = await fetch(proxyUrl);
      if (!response.ok) {
        console.error('Proxy request failed:', {
          status: response.status,
          statusText: response.statusText
        });
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch commentary (${response.status})`);
      }

      const html = await response.text();
      
      // Parse the HTML content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Log the full response for debugging
      console.log('Raw HTML response:', html);

      // Selectors for different commentary sources
      const contentSelectors: Record<string, string[]> = {
        'smith_chuck': ['#cm-content', '.commentary', 'div.study-content', '.study-text'],
        'guzik_david': ['#commentary', '.study-content', '.commentary-content', '.study-guide-content'],
        'mhc': ['#cm-content', '.commentary-content', '.study-content', '.exposition'],
        'jfb': ['#cm-content', '.commentary-text', '.bible-commentary', '.commentary-content']
      };

      const selectors = contentSelectors[source as keyof typeof contentSelectors] || [];
      let commentaryContent = null;

      // Try all selectors
      for (const selector of selectors) {
        const elements = doc.querySelectorAll(selector);
        if (elements.length > 0) {
          commentaryContent = Array.from(elements)
            .map(el => el.textContent)
            .join('\n\n');
          break;
        }
      }

      // If no content found with selectors, try finding main content area
      if (!commentaryContent) {
        const mainContent = doc.querySelector('main') || doc.querySelector('article') || doc.querySelector('.content');
        if (mainContent) {
          commentaryContent = mainContent.textContent;
        }
      }

      if (!commentaryContent) {
        console.error('Content not found. Debug info:', {
          url: blbUrl,
          selectors,
          htmlContent: doc.documentElement.innerHTML
        });
        throw new Error('No commentary content found - Unable to extract content from page');
      }

      // Clean up the content
      const cleanContent = commentaryContent.textContent || '';
      const formattedContent = cleanContent
        .replace(/\s+/g, ' ')
        .trim()
        .split('. ')
        .map(sentence => sentence.trim())
        .filter(sentence => sentence.length > 0)
        .join('. ');

      setCommentary(formattedContent);
      setCommentator(availableSources.find(s => s.id === source)?.name || source);

    } catch (err) {
      console.error('Error fetching commentary:', err);
      setError(err instanceof Error ? err.message : 'Failed to load commentary');
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (sourceId: string) => {
    setSelectedSource(sourceId);
    setShowSourceDropdown(false);
  };

  const handleRefresh = () => {
    if (passage && selectedSource) {
      fetchCommentary(passage, selectedSource);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-sand/20 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <Book className="h-5 w-5 text-electric mr-2" />
            <h2 className="text-lg font-semibold text-midnight">Commentary on {passage}</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate hover:text-midnight rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b border-sand/20 bg-sand/5">
          <div className="flex items-center justify-between">
            <div className="relative">
              <button
                onClick={() => setShowSourceDropdown(!showSourceDropdown)}
                className="px-4 py-2 bg-white border border-sand rounded-lg flex items-center justify-between min-w-[200px]"
              >
                <span>
                  {availableSources.find(s => s.id === selectedSource)?.name || 'Select Source'}
                </span>
                {showSourceDropdown ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
              </button>

              {showSourceDropdown && (
                <div className="absolute left-0 mt-1 w-full bg-white border border-sand rounded-lg shadow-lg z-20">
                  {availableSources.map(source => (
                    <button
                      key={source.id}
                      onClick={() => handleSourceChange(source.id)}
                      className={`w-full text-left px-4 py-2 hover:bg-sand/10 ${
                        selectedSource === source.id ? 'bg-electric/5 text-electric' : ''
                      }`}
                    >
                      {source.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-slate hover:text-electric rounded-lg disabled:opacity-50"
              title="Refresh Commentary"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-electric" />
            </div>
          ) : error ? (
            <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium">Error fetching commentary</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          ) : commentary ? (
            <div>
              {commentator && (
                <h3 className="text-xl font-serif text-midnight mb-4">{commentator}</h3>
              )}
              <div className="prose prose-slate max-w-none">
                {commentary.split('. ').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph.trim() + '.'}</p>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-slate py-12">
              <Book className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Select a passage and commentary source to view insights.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
